import { Language } from "../../data/constants";
import { DocumentReferenceViewModel } from "../../model/DocumentReferenceViewModel";
import { ExamSessionForSignupViewModel, SignupExamSessionExamDaysModel } from "../../model/ExamSignup/ExamSessionForSignupViewModel";
import ModuleAttachmentsViewModel from "../../model/ExamSignup/ModuleAttachmentsViewModel";
import { ModuleExamDetailsViewModel } from "../../model/ExamSignup/ModuleExamDetailsViewModel";
import { ModuleSelectionViewModel } from "../../model/ExamSignup/ModuleSelectionViewModel";
import { SignupModuleRequestModel } from "../../model/ExamSignup/SignupModuleRequestModel";
import { ApiResponse } from "../CustomFetch";
import { api, transformApiResponse } from "./BaseApi";

const signUpApi = api
  .enhanceEndpoints({
    addTagTypes: [
      "ModuleAttachments",
      "AvailableExamsSessions",
      "ExamSessionCandidates",
      "AvailableDays",
      "MyCandidateExams",
      "MyCourses",
      "ModuleAndExamDetails",
      "AvailableLifeExamSessions",
      "AvailableLifeExamSessionDays"
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getExamSessionsAvailableForSignUp: builder.query<ExamSessionForSignupViewModel[], void>({
        query: () => `signup/examSession`,
        transformResponse: transformApiResponse,
        providesTags: ["AvailableExamsSessions"],
      }),

      getLifeExamSessionsAvailableForSignUp: builder.query<
        ExamSessionForSignupViewModel[],
        { language: Language }
      >({
        query: ({ language }) => `signup/life/${language}`,
        transformResponse: transformApiResponse,
        providesTags: ["AvailableLifeExamSessions"],
      }),

      getLifeExamSessionDaysAvailableForSignUp: builder.query<
        SignupExamSessionExamDaysModel[],
        { language: Language, examSessionId: string }
      >({
        query: ({ language, examSessionId }) => `signup/life/${language}/${examSessionId}`,
        transformResponse: transformApiResponse,
        providesTags: ["AvailableLifeExamSessionDays"],
      }),


      getExamSessionAvailableForSignUp: builder.query<
        ExamSessionForSignupViewModel,
        { examSessionId: string; candidateId: string; language: Language; }
      >({
        query: ({ examSessionId, candidateId, language }) => `signup/examSession/${examSessionId}?&candidateId=${candidateId}&language=${language}`,
        transformResponse: transformApiResponse,
        providesTags: ["AvailableExamsSessions"],
      }),

      getModuleExamDetails: builder.query<ModuleExamDetailsViewModel[], ModuleSelectionViewModel[]>({
        query: (moduleSelection: ModuleSelectionViewModel[]) => ({
          url: "signup/moduleExamDetails",
          body: moduleSelection,
          method: "POST",
        }),
        transformResponse: transformApiResponse,
        providesTags: ["ModuleAndExamDetails"],
      }),

      getAllModulesAttachments: builder.query<ModuleAttachmentsViewModel[], void>({
        query: () => ({
          url: "signup/module-attachments",
          method: "GET",
        }),
        transformResponse: transformApiResponse,
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ moduleId }) => ({ type: "ModuleAttachments" as const, id: moduleId })),
                { type: "ModuleAttachments", id: "LIST" },
              ]
            : [{ type: "ModuleAttachments", id: "LIST" }],
      }),

      getModuleAttachments: builder.query<DocumentReferenceViewModel[], string>({
        query: (moduleId) => getModuleAttachmentUrl(moduleId),
        transformResponse: transformApiResponse,
        providesTags: (_result, _error, id) => [{ type: "ModuleAttachments", id }],
      }),

      addModuleAttachment: builder.mutation<ApiResponse<void>, { moduleId: string; file: File }>({
        query: ({ moduleId, file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: getModuleAttachmentUrl(moduleId),
            body: formData,
            method: "POST",
          };
        },
        invalidatesTags: (_result, _error, { moduleId }) => [
          { type: "ModuleAttachments", id: moduleId },
          { type: "ModuleAttachments", id: "LIST" },
        ],
      }),

      deleteModuleAttachment: builder.mutation<ApiResponse<void>, { moduleId: string; fileName: string }>({
        query: ({ moduleId, fileName }) => ({
          url: getModuleAttachmentUrl(moduleId) + `/${fileName}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { moduleId }) => [
          { type: "ModuleAttachments", id: moduleId },
          { type: "ModuleAttachments", id: "LIST" },
        ],
      }),

      signup: builder.mutation<ApiResponse<void>, { candidateId: string; data: SignupModuleRequestModel }>({
        query: ({ candidateId, data }) => {
          const url = "signup/new" + (candidateId === "" ? "" : `?candidateId=${candidateId}`);
          return {
            url: url,
            body: data,
            method: "POST",
          };
        },
        invalidatesTags: [
          "AvailableExamsSessions",
          "ExamSessionCandidates",
          "AvailableDays",
          "MyCandidateExams",
          "MyCourses",
        ],
      }),
    }),
  });

export const {
  useGetExamSessionsAvailableForSignUpQuery,
  useGetLifeExamSessionsAvailableForSignUpQuery,
  useGetLifeExamSessionDaysAvailableForSignUpQuery,
  useGetExamSessionAvailableForSignUpQuery,
  useGetModuleExamDetailsQuery,
  useGetAllModulesAttachmentsQuery,
  useGetModuleAttachmentsQuery,
  useAddModuleAttachmentMutation,
  useDeleteModuleAttachmentMutation,
  useSignupMutation,
} = signUpApi;

export const getModuleAttachmentUrl = (moduleId: string) => `signup/module-attachments/${moduleId}`;
